<script setup lang="ts">
  import { defineAsyncComponent } from 'vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { Dialog, DialogScrollContent } from '@/components/ui/dialog'

  const LegacyCaseDetailPage = defineAsyncComponent(
    () => import('@/views/app/cases/legacy/LegacyCaseDetailPageWrapper.vue')
  )

  const props = defineProps<{
    open: boolean
    caseUuid: string | null
    caseLabel: string
  }>()

  const emit = defineEmits<{
    (e: 'update:open', value: boolean): void
    (e: 'addToChat', uuid: string, label: string): void
  }>()

  function onAddToChat() {
    if (!props.caseUuid) return
    emit('addToChat', props.caseUuid, props.caseLabel)
    emit('update:open', false)
  }

  const { t } = useTranslation({
    en: {
      addContext: 'Add context',
      cancel: 'Cancel',
      addToChat: 'Add to chat'
    },
    de: {
      addContext: 'Kontext hinzufügen',
      cancel: 'Abbrechen',
      addToChat: 'Zum Chat hinzufügen'
    },
    fr: {
      addContext: 'Ajouter le contexte',
      cancel: 'Annuler',
      addToChat: 'Ajouter au chat'
    }
  })
</script>

<template>
  <Dialog
    :open="props.open"
    @update:open="emit('update:open', $event)"
  >
    <DialogScrollContent
      overlay-class="z-[70]"
      class="case-preview-dialog z-[70] max-w-[1050px] gap-0 overflow-hidden rounded-2xl border-0 bg-white p-0
        shadow-[0px_4px_8px_0px_rgba(0,0,0,0.08),0px_8px_12px_0px_rgba(0,0,0,0.08)]"
    >
      <!-- Header bar -->
      <div class="flex items-center justify-between border-b border-gray-100 p-4">
        <div class="text-base font-medium leading-6 text-gray-800 font-inter">
          {{ t('addContext') }}
        </div>
        <div class="flex items-center gap-1.5">
          <button
            type="button"
            class="h-8 rounded-md bg-white px-3 py-1.5 text-sm font-medium leading-5 text-gray-800
              shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] outline outline-1 -outline-offset-1 outline-gray-200
              font-inter hover:bg-gray-50"
            @click="emit('update:open', false)"
          >
            {{ t('cancel') }}
          </button>
          <button
            type="button"
            class="h-8 rounded-md bg-primary px-3 py-1.5 text-sm font-medium leading-5 text-white
              shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] font-inter hover:bg-primary/90"
            @click="onAddToChat"
          >
            {{ t('addToChat') }}
          </button>
        </div>
      </div>

      <!-- Embedded case detail page -->
      <div
        v-if="props.caseUuid"
        class="case-preview-content max-h-[calc(100vh-10rem)] overflow-y-auto overflow-x-hidden bg-gray-50"
      >
        <LegacyCaseDetailPage :case-uuid="props.caseUuid" />
      </div>
    </DialogScrollContent>
  </Dialog>
</template>

<style>
  /* Hide the default DialogClose X button rendered by DialogScrollContent */
  .case-preview-dialog > [data-radix-collection-item] {
    display: none !important;
  }

  /* Fallback: hide any absolutely positioned close button at top-right */
  .case-preview-dialog > .absolute.right-3.top-3 {
    display: none !important;
  }

  /* The duplicate banner uses a full-viewport-width trick (-ml-[50vw] w-screen)
     that overflows inside a constrained modal. Reset to fill the modal width. */
  .case-preview-content .w-screen.-ml-\[50vw\] {
    position: relative !important;
    left: auto !important;
    right: auto !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    width: 100% !important;
  }

  /* SpaCompatComponent sets min-h-[calc(100vh-60px)] which creates whitespace in a modal.
     Remove the min-height constraint when embedded. */
  .case-preview-content .min-h-\[calc\(100vh-60px\)\] {
    min-height: auto !important;
  }
</style>
